import { useState, useEffect, useContext, useRef, useMemo, useCallback, createRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import blue from '@material-ui/core/colors/blue';
import grey from '@material-ui/core/colors/grey';
import Paper from '@material-ui/core/Paper';
import ReactResizeDetector from 'react-resize-detector';
import { serverContext, appContext, fetchFromServer, getFromServer, postToServer, callServer, downloadFile, themeContext } from '../App';
import UavMap, { MapCenterMode, MapControlsMode, AoiMode } from '../Map/UavMap';
import FloatingWnd from './FloatingWnd';
import VideoCoverageChart from './VideoCoverageChart';
import { StCutControl, StBookmarksTimeline, KlvView, SensorPlayer, SensorPlayerToolbar, useMission, useSensors, useSyncTimeline, timelineToClipPos, getNextClipIndex } from 'stserver-frontend-comp';
import CreateBookmarkDlg from '../CreateBookmarkDlg';
import CreateSnapshotDlg from '../CreateSnapshotDlg';
import BookmarksDlg from '../BookmarksDlg';
import * as moment from 'moment';
import CurrentMissionTimeline from './CurrentMissionTimeline';
import { useAisData, useAisTrail, useAnnotationData } from '../customEffects';
import LivePlayer from './LivePlayer';
import { getLiveDvrWarmupMessage, getSafeLiveMode, mapLiveDvrError } from './liveDvr';

const ResizeAwareGrid = props => {
  const { targetRef, ...rest } = props;
  return <Grid ref={targetRef} {...rest} />;
};


const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  playerWrapper: {
    background: grey[400],
    padding: 1
  },
  masterPlayerWrapper: {
    background: blue[200],
    padding: 1
  },
  videoCoverageChart: {
    background: theme.palette.background.paper,
    padding: 2,
    marginTop: 4,
    height: 80
  },
  telemetryContainer: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    background: theme.palette.background.paper,
  },
  bookmarksChart: {
    position: 'relative',
    background: 'green',
    padding: 2,
    marginTop: 4,
    height: 40
  },
  playerBottomControls: {
    position: 'static',
    background: theme.palette.background.paper,
    marginTop: 4,
    marginBottom: 4,
  },
  timeline: {
    marginTop: theme.spacing(0.5),
  },

}));

const DEFAULT_MAP_CENTER = [37.71478815132924, -122.44674682617188];

const toLatLngArray = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    const lat = Number(value[0]);
    const lng = Number(value[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
    return null;
  }
  if (value && typeof value === 'object') {
    const lat = Number(value.lat ?? value.latitude);
    const lng = Number(value.lng ?? value.lon ?? value.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }
  return null;
};

const ensureLatLngArray = (value, fallback = DEFAULT_MAP_CENTER) => {
  const normalized = toLatLngArray(value);
  return normalized ? normalized : [...fallback];
};

const getLastLocation = type => {
  const lastFloatingWinLocation = localStorage.getItem(`lastFloatingWinLocation-${type}`);
  return lastFloatingWinLocation != null ? JSON.parse(lastFloatingWinLocation) : {
    width: 160,
    height: 240,
    x: 10,
    y: 10
  };
}

const saveLastLocation = (type, location) => {
  localStorage.setItem(`lastFloatingWinLocation-${type}`, JSON.stringify(location));
}

const getLastMapProps = () => {
  const lastMapProp = localStorage.getItem('lastMapProps');
  let mProps = lastMapProp != null ? JSON.parse(lastMapProp) : {
    center: [...DEFAULT_MAP_CENTER],
    zoom: 12,
    mapCenterMode: MapCenterMode.Platform,
    mapControlsMode: MapControlsMode.None,
    aoiControlsMode: AoiMode.FrameCenter
  };

  mProps.center = ensureLatLngArray(mProps.center);

  if (mProps.mapCenterMode === undefined)
    mProps.mapCenterMode = MapCenterMode.Platform;

  if (mProps.aoiControlsMode === undefined)
    mProps.aoiControlsMode = AoiMode.FrameCenter;

  return mProps;
}

const saveLastMapProps = mapProps => {
  localStorage.setItem('lastMapProps', JSON.stringify(mapProps));
}

const getLiveMode = props => {
  if (props.useLiveLowLatency) return "lowLatency";
  if (props.useWebRtcVideo) return "webRtc";
  if (props.useLiveRtspHls) return "highQuality";
  return "highQualityDvr";
}

const findLiveSensorById = (sensors, id) =>
  sensors.find(s => `${s.parentPlatformName}/${s.name}` === id);

const calculatePlayerWidth = (gridMapOpen, containerWidth, sensorsCount) => {
  if (containerWidth < 600) return containerWidth * 0.98;
  let k = 0.48;
  if (gridMapOpen) {
    if (sensorsCount === 0) k = 0.6;
  } else {
    if (sensorsCount === 1) k = 0.72;
  }
  return containerWidth * k;
};

const getMissionClipsPosition = (mission, sensors) => {
  if (mission && sensors) {
    const missionStart = new Date(mission.startTime).getTime();
    return sensors.map(s => {
      const sClips = s.clips.map(c => ({
        start: (new Date(c.startTime).getTime() - missionStart) / 1000.0,
        end: (new Date(c.endTime).getTime() - missionStart) / 1000.0
      }));
      return sClips;
    });
  }
  return null;
}

const getMissionStartTimeStamp = mission => {
  if (mission) return new Date(mission.startTime).getTime();
  return null;
}

const setMasterSensorTimeMinInterval = 1.0; // seconds
const defaultAspectRatio = 16 / 9;
const MAP_DEFAULT_HEIGHT = 480;

function MissionPlayerGrid(props) {
  const { server } = useContext(serverContext);
  const { app, dispatchApp } = useContext(appContext);
  const themeCtx = useContext(themeContext);
  const themeMode = themeCtx?.mode === 'dark' ? 'dark' : 'light';
  const [klvData, setKlvData] = useState({});
  const [klvWndLocation, setKlvWndLocation] = useState(() => getLastLocation('klv'));
  const [mapWndLocation, setMapWndLocation] = useState(() => getLastLocation('floatingMap'));
  const [playerWidth, setPlayerWidth] = useState(640);
  const [containerWidth, setContainerWidth] = useState(840);
  const [sensorsHeights, setSensorsHeights] = useState({});
  // containerMaxHeight removed because it's not used elsewhere; keep sensorsHeights instead
  const [sensorsAspectRatios, setSensorsAspectRatios] = useState({});
  const [mapHeight, setMapHeight] = useState(MAP_DEFAULT_HEIGHT);
  const [mapProps, setMapProps] = useState(() => getLastMapProps());
  const [mapPlatforms, setMapPlatforms] = useState([]);
  const [mapDraggable, setMapDraggable] = useState(true);
  const [videoQueryChart, setVideoQueryChart] = useState(null);
  const [currentMapControlsMode, setCurrentMapControlsMode] = useState(props.selectMapControl ? props.selectMapControl : MapControlsMode.None);
  const [currentAoiControlsMode, setCurrentAoiControlsMode] = useState(mapProps.aoiControlsMode);
  const [liveSensorsMode, setLiveSensorsMode] = useState({});
  const liveDvrFallbackMode = useRef({});
  const [placeholderHeight, setPlaceholderHeight] = useState(640 / defaultAspectRatio);

  const gridMapControl = useRef({});
  const floatingMapControl = useRef({});
  const masterSensor = useRef(null);
  const [masterSensorName, setMasterSensorName] = useState(null);
  const masterSensorIndex = useRef(0);
  const [masterSensorTime, setMasterSensorTime] = useState(null);

  // --- Measurement plumbing for first sensor block ---
  const firstSensorNodeRef = useRef(null);
  const resizeObsRef = useRef(null);
  const rafIdRef = useRef(null);
  const lastMapHeightRef = useRef(mapHeight);

  useEffect(() => { lastMapHeightRef.current = mapHeight; }, [mapHeight]);

  const measureNodeTotalHeight = (node) => {
    if (!node) return null;

    // In live mode we wrap the LivePlayer; measure its first child (the Paper block)
    const target = node.dataset.measureInner === '1' && node.firstElementChild
      ? node.firstElementChild
      : node;

    // Use offsetHeight (includes padding+border), then add computed margins
    const base = target.offsetHeight || 0;
    const cs = window.getComputedStyle(target);
    const mt = parseFloat(cs.marginTop) || 0;
    const mb = parseFloat(cs.marginBottom) || 0;
    const total = Math.round(base + mt + mb);
    return total > 0 ? total : null;
  };

  const attachObserver = useCallback((node) => {
    // Disconnect previous observer & cancel pending rAF
    if (resizeObsRef.current) { try { resizeObsRef.current.disconnect(); } catch { } resizeObsRef.current = null; }
    if (rafIdRef.current) { try { cancelAnimationFrame(rafIdRef.current); } catch { } rafIdRef.current = null; }

    firstSensorNodeRef.current = node;
    if (!node) return;

    // Make an initial measurement immediately
    const initial = measureNodeTotalHeight(node);
    if (initial && initial !== lastMapHeightRef.current) setMapHeight(initial);

    const ro = new ResizeObserver(() => {
      const h = measureNodeTotalHeight(node);
      if (h && h !== lastMapHeightRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          setMapHeight(prev => (prev !== h ? h : prev));
        });
      }
    });

    // Observe the outer node; if dataset.measureInner=1 the measurement uses its first child
    ro.observe(node);
    resizeObsRef.current = ro;
  }, []);

  // Callback ref used in JSX
  const setFirstSensorNode = useCallback((node) => { attachObserver(node); }, [attachObserver]);

  // Reset to default when no sensors
  useEffect(() => {
    if (!props.sensors || props.sensors.length === 0) {
      if (resizeObsRef.current) { try { resizeObsRef.current.disconnect(); } catch { } resizeObsRef.current = null; }
      setMapHeight(MAP_DEFAULT_HEIGHT);
    }
  }, [props.sensors.length]);

  useEffect(() => {
    return () => {
      if (resizeObsRef.current) { try { resizeObsRef.current.disconnect(); } catch { } }
      if (rafIdRef.current) { try { cancelAnimationFrame(rafIdRef.current); } catch { } }
    };
  }, []);
  // --- end measurement plumbing ---

  const { mission } = useMission(server.serverHost, props.mission, server.token, props.missionUpdateTime);
  const { sensors: mSensors } = useSensors(server.serverHost, props.mission, server.token, props.missionUpdateTime);

  const lastMasterKlvTimestamp = useRef(null);
  const players = useMemo(() => props.sensors.map(p => createRef()), [props.sensors]);
  const missionClipPositions = useMemo(() => getMissionClipsPosition(mission, mSensors), [mission, mSensors]);
  const missionStartTimestamp = useMemo(() => getMissionStartTimeStamp(mission), [mission]);
  const timelinePos = useSyncTimeline(
    props.mode !== 'live' ? missionStartTimestamp : undefined,
    props.mode !== 'live' ? missionClipPositions : undefined,
    props.mode !== 'live' ? players : undefined,
    props.mode !== 'live' ? masterSensorIndex.current : undefined,
    props.mode !== 'live' ? masterSensorTime : undefined,
    props.mode !== 'live' ? 0.5 : undefined
  );

  const klvViewerOpen = props.controls && props.controls.includes('klv');
  const mapViewerOpen = props.controls && props.controls.includes('floatingMap');
  const gridMapOpen = props.controls && props.controls.includes('gridMap');
  const bookmarksChartOpen = props.controls && props.controls.includes('bookmarks');
  const exportVideotOpen = props.controls && props.controls.includes('exportVideo');
  const [createBookmarkOpen, setCreateBookmarkOpen] = useState(false);
  const [createSnapshotOpen, setCreateSnapshotOpen] = useState(false);
  const [bookmarksDlgOpen, setBookmarksDlgOpen] = useState(false);
  const [bookmarksUpdateTime, setBookmarksUpdateTime] = useState(Date.now());

  // ais
  const [aisUpdate, setAisUpdate] = useState(Date.now());
  const [aisFilter, setAisFilter] = useState({ mode: 'last', aisType: 'vessels', info: 'true' });
  const { aisData } = useAisData(server.serverHost, server.token, aisFilter, aisUpdate);
  const { selectedAisTrail } = useAisTrail(server.serverHost, server.token, props.selectedAis, aisFilter /*, aisTrailUpdate*/);

  // annotations
  const [fetchAnnotationsFilter, setFetchAnnotationsFilter] = useState({});
  const [rawAnnotationsUpdate, setRawAnnotationsUpdate] = useState();
  const { annotationData: rawAnnotationData } = useAnnotationData(server.serverHost, server.token, fetchAnnotationsFilter, rawAnnotationsUpdate);
  const [annotationData, setAnnotationData] = useState([]);

  useEffect(() => {
    const normalizedCenter = toLatLngArray(props.mapCenter?.center);
    if (normalizedCenter) {
      setMapProps(prev => ({ ...prev, center: normalizedCenter }));
    }
  }, [props.mapCenter]);

  useEffect(() => {
    setRawAnnotationsUpdate(Date.now());
  }, [props.annotationUpdate, server?.annotationsUpdate]);

  useEffect(() => {
    const setWidth = () => {
      const curPlayerWidth = calculatePlayerWidth(gridMapOpen, containerWidth, props.sensors.length);
      setPlayerWidth(curPlayerWidth);
      setPlaceholderHeight(curPlayerWidth / defaultAspectRatio);
    }
    setWidth();
  }, [props.sensors.length, containerWidth, gridMapOpen]);

  useEffect(() => {
    const curPlayerWidth = calculatePlayerWidth(gridMapOpen, containerWidth, props.sensors.length);

    let heights = {};
    let aspectRatios = {};
    let nextLiveSensorKeys = {};

    if (props.mode === 'vod' && props.sensors.length > 0) {
      props.sensors.forEach(s => {
        const aspectRatio = s.videoInfo ? s.videoInfo.width / s.videoInfo.height : defaultAspectRatio;
        heights[`${s.m}/${s.s}`] = curPlayerWidth / aspectRatio;
        aspectRatios[`${s.m}/${s.s}`] = aspectRatio;
      })
      masterSensor.current = `${props.sensors[0].m}/${props.sensors[0].s}`;
      setMasterSensorName(`${props.sensors[0].m}/${props.sensors[0].s}`);
    }

    if (props.mode === 'live' && props.sensors.length > 0) {
      const defaultLiveMode = getLiveMode(props);
      props.sensors.forEach(s => {
        const videoInfo = s.streamInfo ? s.streamInfo.streams.filter(s => s.codec_type === 'video') : null;
        const aspectRatio = Array.isArray(videoInfo) ? videoInfo[0].width / videoInfo[0].height : defaultAspectRatio;
        heights[`${s.parentPlatformName}/${s.name}`] = placeholderHeight;
        aspectRatios[`${s.parentPlatformName}/${s.name}`] = aspectRatio;
        nextLiveSensorKeys[`${s.parentPlatformName}/${s.name}`] = true;
      })
      masterSensor.current = `${props.sensors[0].parentPlatformName}/${props.sensors[0].sensor}`;
      setMasterSensorName(`${props.sensors[0].parentPlatformName}/${props.sensors[0].sensor}`);

      setLiveSensorsMode(prevLiveSensorsMode => {
        const nextLiveSensorsMode = {};

        props.sensors.forEach(s => {
          const sensorKey = `${s.parentPlatformName}/${s.name}`;
          const currentMode = prevLiveSensorsMode[sensorKey];
          const safeDefaultMode = getSafeLiveMode(defaultLiveMode, s);

          if (!currentMode) {
            nextLiveSensorsMode[sensorKey] = safeDefaultMode;
            liveDvrFallbackMode.current[sensorKey] = defaultLiveMode === 'highQualityDvr' && safeDefaultMode !== defaultLiveMode;
            return;
          }

          const safeCurrentMode = getSafeLiveMode(currentMode, s);
          const shouldRestoreDvr =
            defaultLiveMode === 'highQualityDvr' &&
            safeDefaultMode === 'highQualityDvr' &&
            currentMode === 'highQuality' &&
            liveDvrFallbackMode.current[sensorKey];

          nextLiveSensorsMode[sensorKey] = shouldRestoreDvr ? 'highQualityDvr' : safeCurrentMode;

          if (shouldRestoreDvr) {
            liveDvrFallbackMode.current[sensorKey] = false;
          } else if (currentMode === 'highQualityDvr' && safeCurrentMode !== currentMode) {
            liveDvrFallbackMode.current[sensorKey] = true;
          }
        });

        Object.keys(liveDvrFallbackMode.current).forEach(sensorKey => {
          if (!nextLiveSensorKeys[sensorKey]) {
            delete liveDvrFallbackMode.current[sensorKey];
          }
        });

        return nextLiveSensorsMode;
      });
    } else {
      setLiveSensorsMode({});
      liveDvrFallbackMode.current = {};
    }

    setSensorsHeights(heights);
    setSensorsAspectRatios(aspectRatios);

    let minAspectRatio = defaultAspectRatio;
    for (let [, value] of Object.entries(aspectRatios)) {
      if (value < minAspectRatio) minAspectRatio = value;
    }
  }, [props.sensors, props.mode, containerWidth, gridMapOpen, placeholderHeight]);

  useEffect(() => {
    if (masterSensor.current && masterSensorIndex.current !== undefined) {
      setCreateSnapshotOpen(props.takeSnapshot);
    } else {
      props.onSnapshot && props.onSnapshot();
    }
  }, [props.takeSnapshot, masterSensor.current, masterSensorIndex.current]);

  useEffect(() => {
    const hAnnotations = JSON.parse(localStorage.getItem('hiddenAnnotations')) || [];

    if (rawAnnotationData && rawAnnotationData.length > 0) {
      const visibleAnnotations = hAnnotations.length > 0 ? rawAnnotationData.filter(item => !hAnnotations.includes(item._id)) : rawAnnotationData;
      setAnnotationData([...visibleAnnotations]);
    } else {
      setAnnotationData([]);
    }
  }, [rawAnnotationData, fetchAnnotationsFilter, props.annotationUpdate]);

  useEffect(() => {
    if (!server.useAis) return;
    const interval = setInterval(() => {
      try {
        const saved = localStorage.getItem('aisQueryFilter');
        const parsed = saved !== null ? JSON.parse(saved) : {};

        if (props.mode === 'live') {
          setAisFilter({
            ...parsed,
            start: new Date(Date.now() - parsed.retention * 60 * 1000).toISOString(),
            end: new Date().toISOString(),
          });
        } else {
          if (lastMasterKlvTimestamp.current != null) {
            setAisFilter({
              ...parsed,
              start: new Date(lastMasterKlvTimestamp.current / 1000 - parsed.retention * 60 * 1000).toISOString(),
              end: new Date(lastMasterKlvTimestamp.current / 1000).toISOString()
            });
          }
        }
        setAisUpdate(Date.now());
      } catch (error) { }
    }, 2000);
    return () => clearInterval(interval);
  }, [server.useAis, props.mode]);

  const handleResolutionDetection = (source, width, height) => {
    sensorsAspectRatios[`${source}`] = width / height;
    setSensorsAspectRatios({ ...sensorsAspectRatios });
  }

  const handleDimensionsChange = (source, width, height) => {
    sensorsHeights[`${source}`] = height;
    let maxContainerHeight = 0;
    for (let [, value] of Object.entries(sensorsHeights)) {
      if (value > maxContainerHeight) maxContainerHeight = value;
    }
    // maxContainerHeight is calculated for potential future use but currently not needed.
    // We keep sensorsHeights updated; avoid storing unused state to satisfy linter.
  }

  const handleLocationChange = (type, location) => {
    saveLastLocation(type, location);
    switch (type) {
      case 'klv':
        setKlvWndLocation(location);
        break;
      case 'gridMap':
        break;
      case 'floatingMap':
        setMapWndLocation(location);
        break;
      default:
        break;
    }
  }

  const handleKlvDataReceived = (source, i, platformType, data) => {
    if (data) {
      const t = data['2'];

      if (players[i].current) {
        players[i].current.klvTime = t;
      }

      if (source === masterSensor.current) {
        setKlvData(data);

        if (masterSensorIndex.current === i) {
          if (lastMasterKlvTimestamp.current == null) {
            lastMasterKlvTimestamp.current = t;
          } else if (Math.abs(t - lastMasterKlvTimestamp.current) / 1000000 >= setMasterSensorTimeMinInterval) {
            lastMasterKlvTimestamp.current = t;
            setMasterSensorTime(t);
          }
        }

        if (data['13'] !== undefined && data['14'] !== undefined) {
          setMapPlatforms([{
            name: source,
            platformType: platformType ? platformType.toLowerCase() : 'plane',
            klvs: data
          }])
        }
      }
    }
  }

  const handleTimelineCrosshairClick = (tlPos) => {
    const masterPlayer = players[masterSensorIndex.current].current;

    if (!Array.isArray(missionClipPositions) || missionClipPositions.length === 0 || missionClipPositions[0].length === 0) {
      masterPlayer.currentTime(tlPos);
    }
    else {
      const { clipIndex, clipPos, outOfRange } = timelineToClipPos(missionClipPositions, masterSensorIndex.current, tlPos);

      if (!outOfRange) {
        masterPlayer.playlist.currentItem(clipIndex);
        masterPlayer.currentTime(clipPos);
      }
      else {
        setTimeout(() => {
          const { nextClipIndex, outOfRange } = getNextClipIndex(missionClipPositions, masterSensorIndex.current, tlPos);
          if (!outOfRange) {
            masterPlayer.playlist.currentItem(nextClipIndex);
            masterPlayer.currentTime(0);
          }
        }, 100);
      }
    }
  }

  const handlePlayerClick = (event, name, index) => {
    masterSensor.current = name;
    masterSensorIndex.current = index;
    setMasterSensorName(name);
    event.stopPropagation();
  }

  const handleMapClose = updatedMapProps => {
    const sanitized = {
      ...updatedMapProps,
      center: ensureLatLngArray(updatedMapProps.center, mapProps.center)
    };
    setMapProps(sanitized);
    saveLastMapProps(sanitized);
  }

  const handleMapMouseOver = () => { setMapDraggable(false); }
  const handleMapMouseLeave = () => { setMapDraggable(true); }

  const getCombinedSensors = () => {
    const combinedArray = [];
    if (props.mode === 'vod') {
      return props.sensors;
    } else {
      const liveSensorsTr = props.sensors.map(ls => ({ m: ls.mission, s: ls.sensor }));
      combinedArray.push(...liveSensorsTr);
    }
    return combinedArray;
  }

  const handleShowHeatmapClick = () => new Promise(async (resolve, reject) => {
    const combinedArray = getCombinedSensors();
    if (combinedArray.length > 0) {
      let hPoints = [];
      for (const s of combinedArray) {
        try {
          const hP = await fetchFromServer(`sensors/framecenterpoints/${s.m}/${s.s}`, server)
          hPoints = [...hPoints, ...hP.data];
        } catch (error) { }
      }
      resolve(hPoints);
    }
    else {
      const errorMessage = "No sensors selected."
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: errorMessage, severity: 'error' } });
      reject(new Error(errorMessage))
    }
  })

  const handleShowAreaClick = () => new Promise(async (resolve, reject) => {
    const combinedArray = getCombinedSensors();
    if (combinedArray.length > 0) {
      let mArea;
      try {
        const res = await fetchFromServer(`missions/area/${combinedArray[0].m}`, server);
        mArea = res.data;
      } catch (error) {
        const errorMessage = `Fetching area error - ${error.response && error.response.data ? error.response.data : error.message}`;
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: errorMessage, severity: 'error' } });
        reject(new Error(errorMessage));
        return;
      }
      resolve(mArea);
    } else {
      const errorMessage = 'No mission selected.';
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: errorMessage, severity: 'error' } });
      reject(new Error(errorMessage))
    }
  })

  const handleShowPathClick = () => new Promise(async (resolve, reject) => {
    const combinedArray = getCombinedSensors();
    if (combinedArray.length > 0) {
      const paths = [];
      const errors = [];
      for (const s of combinedArray) {
        try {
          const sPath = await fetchFromServer(`sensors/path/${s.m}/${s.s}`, server);
          if (sPath.data?.coordinates?.length > 0) {
            paths.push({
              type: 'Feature',
              properties: sPath.data.properties || { missionName: s.m, sensorName: s.s },
              geometry: {
                type: sPath.data.type,
                coordinates: sPath.data.coordinates
              }
            });
          }
        } catch (error) {
          errors.push(error.response && error.response.data ? error.response.data : error.message);
        }
      }

      if (paths.length === 0) {
        const errorMessage = `Error getting path - ${errors[0] || 'No platform location points found'}`;
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: errorMessage, severity: 'error' } });
        return reject(new Error(errorMessage))
      }

      resolve(paths.length === 1 ? paths[0] : {
        type: 'FeatureCollection',
        features: paths
      });
    } else {
      const errorMessage = "No sensors selected."
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: errorMessage, severity: 'error' } });
      reject(new Error(errorMessage))
    }
  })

  const handleMapControlsModeChange = async mode => {
    switch (currentMapControlsMode) {
      case MapControlsMode.None:
        break;
      case MapControlsMode.QueryMission:
        setVideoQueryChart(null);
        break;
      case MapControlsMode.FilterMissions:
        const filter = { ...app.missionsFilter, aoiMode: undefined, geometry: undefined };
        if (props.onApplyQueryFilter) props.onApplyQueryFilter(filter);
        break;
      case MapControlsMode.DrawAnnotations:
        break;
      default:
        break;
    }
    setCurrentMapControlsMode(mode);
  }

  const handleAoiControlsModeChange = async mode => {
    setCurrentAoiControlsMode(mode);
  }

  const queryServer = (mapControlsMode, geoJson) => new Promise(async (resolve) => {
    try {
      switch (mapControlsMode) {
        case MapControlsMode.QueryMission:
          if (masterSensor.current) {
            setVideoQueryChart({});
            const resVideoList = await getFromServer(`sensors/videolist/${masterSensor.current}`, { geometry: geoJson, aoiMode: currentAoiControlsMode, start: undefined, end: undefined }, server);
            setVideoQueryChart({ id: masterSensor.current, data: resVideoList.data });
          } else {
            dispatchApp({ type: 'SHOW_ALERT', payload: { title: 'Error', message: `Please select mission to query.` } });
          }
          break;
        case MapControlsMode.FilterMissions:
          const filter = { ...app.missionsFilter, geometry: geoJson, aoiMode: currentAoiControlsMode };
          if (props.onApplyQueryFilter) props.onApplyQueryFilter(filter);
          break;
        case MapControlsMode.DrawAnnotations:
          break;
        default:
          break;
      }
    } catch (error) {
      dispatchApp({ type: 'SHOW_ALERT', payload: { title: 'Error', message: `${error.message}. ${error?.response?.data ?? ''}` } });
    }
    resolve();
  })

  const handleDrawAction = (mapControlsMode, eventType, geoJson) => new Promise(async (resolve) => {
    try {
      switch (eventType) {
        case 'draw:created':
        case 'draw:edited':
          await queryServer(mapControlsMode, geoJson);
          break;
        case 'draw:deleted':
          setVideoQueryChart(null);
          break;
        default:
          break;
      }
    } catch (error) { }
    resolve();
  })

  const seekTo = (i, position) => {
    const player = players[i].current;
    if (player) {
      const seekable = player.seekable();
      if (seekable && seekable.length) {
        if (position < 0) { player.currentTime(0); player.pause(); return; }
        const end = seekable.end(0);
        if (position > end) { player.currentTime(end); return; }
        player.currentTime(position);
        player.play();
      }
    }
  }

  const handleVideoCoverageChartClick = (i, pos) => {
    const { offset } = pos;
    seekTo(i, offset);
  }

  const handleBookmarkChartClick = (i, position) => {
    seekTo(i, position);
  }

  const handleBookmarkTimelineClick = (sensorName, bm) => {
    const i = props.sensors.findIndex(s => s.s === sensorName);
    seekTo(i, bm.position);
  }

  const handleLiveModeControlChange = (id, mode) => {
    const sensor = findLiveSensorById(props.sensors, id);
    const nextMode = getSafeLiveMode(mode, sensor);
    const dvrWarmupFallback = mode === 'highQualityDvr' && nextMode !== mode;

    if (dvrWarmupFallback) {
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: getLiveDvrWarmupMessage(), severity: 'warning' } });
    }

    liveDvrFallbackMode.current[id] = dvrWarmupFallback;
    setLiveSensorsMode(prevLiveSensorsMode => ({ ...prevLiveSensorsMode, [id]: nextMode }));
  }

  const handleLiveModeControlError = error => {
    const rawMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
    const message = mapLiveDvrError(rawMessage);
    dispatchApp({
      type: 'SHOW_SNACKBAR_MESSAGE',
      payload: {
        message,
        severity: message === rawMessage ? 'error' : 'warning'
      }
    });
  }

  const handleAddBookmark = () => {
    setCreateBookmarkOpen(true);
  }

  const handleDeleteBookmarks = async bookmarks => {
    if (masterSensorIndex.current !== undefined) {
      if (Array.isArray(bookmarks) && bookmarks.length > 0) {
        try {
          const bmIds = bookmarks.map(b => b._id);
          const ret = await callServer('delete', 'bookmarks', { bookmarks: bmIds }, server);
          if (ret.status === 200) dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Bookmark(s) ${bookmarks.map(b => b.title).join(', ')} deleted.` } });
          setTimeout(() => { setBookmarksUpdateTime(Date.now()); }, 500);
        } catch (error) {
          dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Error deleting bookmarks ${error.message} - ${error.request ? error.request.statusText : ''}`, severity: 'error' } });
        }
      }
    } else {
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'Master sensor is not selected', severity: 'error' } });
    }
  }

  const handleCloseCreateBookmark = () => { setCreateBookmarkOpen(false); }

  const handleCreateBookmark = async bm => {
    try {
      if (masterSensorIndex.current !== undefined) {
        const id = masterSensor.current.split('/');
        const newBookmark = { missionIdOrName: id[0], sensorIdOrName: id[1], ...bm }
        const ret = await postToServer('bookmarks', newBookmark, server);
        if (ret.status === 200) dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Bookmark ${bm.title} added.` } });
        setTimeout(() => { setBookmarksUpdateTime(Date.now()); }, 200);
      } else {
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'Master sensor is not selected', severity: 'error' } });
      }
    } catch (error) {
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `${error.message}. ${error?.response?.data}`, severity: 'error' } });
    }
    setCreateBookmarkOpen(false);
  }

  const handleEditBookmarks = bookmarks => { setBookmarksDlgOpen(!bookmarksDlgOpen); }
  const handleCloseEditBookmarks = () => { setBookmarksDlgOpen(false); }

  const handleContainerResize = (width /*, height*/) => { setContainerWidth(width); }

  const masterSlavePlayerWrapper = id => masterSensor.current === id ? classes.masterPlayerWrapper : classes.playerWrapper;

  const handleSnapshotCreate = async (snapshot) => {
    try {
      if (masterSensorIndex.current !== undefined) {
        const id = masterSensor.current.split('/');
        let newSnapshot;
        const sn = {
          snapshotDataUrl: snapshot.snapshotDataUrl,
          timestamp: snapshot.startTime,
          title: snapshot.title,
          description: snapshot.description,
          tags: snapshot.tags,
          usergroups: snapshot.usergroups,
          restricted: snapshot.restricted
        }
        if (props.mode === 'live' && props.sensors.length > 0) {
          newSnapshot = { platformName: snapshot.platformName, sensorName: snapshot.sensorName, missionName: snapshot.missionName, ...sn }
        } else {
          newSnapshot = { missionName: id[0], sensorName: id[1], platformName: undefined, ...sn }
        }
        const ret = await postToServer('snapshots', newSnapshot, server);
        if (ret.status === 200) dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Snapshot ${snapshot.title} created.` } });
      } else {
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'Master sensor is not selected', severity: 'error' } });
      }
    } catch (error) {
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `${error.message}. ${error?.response?.data}`, severity: 'error' } });
    }
    handleSnapshotClose();
  }

  const handleSnapshotClose = () => { props.onSnapshotClose && props.onSnapshotClose(); }
  const onAisSelected = props.onAisSelected;
  const handleAisSelected = useCallback((ais) => {
    if (onAisSelected) {
      onAisSelected(ais);
    }
  }, [onAisSelected]);
  const handleAnnotationSelected = (annotationId) => { props.onAnnotationSelected && props.onAnnotationSelected(annotationId); }
  const handleMapBoundingBoxChange = (bbox, mapCenter) => {
    setFetchAnnotationsFilter({ ...fetchAnnotationsFilter, bbox: bbox });
    props.onMapBoundingBoxChange && props.onMapBoundingBoxChange(bbox, mapCenter);
  }

  const handleExportVideo = (mi, si, vid) => {
    if (server.userRole) {
      if (server.username === 'guest') {
        const message = `Guest users do not have permission to download the video.`;
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: message, severity: 'warning' } });
        return;
      }
    }
    try {
      const clipName = `${mi}-${si}-${vid.markIn}-${vid.markOut}.ts`;
      const message = `Exporting clip ${clipName}...`;
      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: message } });
      downloadFile(server, `sensors/exportVideo/${mi}/${si}?markIn=${vid.markIn}&markOut=${vid.markOut}`, clipName);
    } catch (error) {
      console.log(error);
    }
  }

  const classes = useStyles();
  return (
    <ReactResizeDetector handleHeight={false} onResize={handleContainerResize}>
      <ResizeAwareGrid key='sensors' container className={classes.root} spacing={2}>
        <Grid container justifyContent="center" spacing={1} alignItems="flex-start">

          {props.mode === 'vod' && props.sensors.filter(s => s.type === 'video').map((s, i) => (
            <Grid key={`${s.m}/${s.s}`} item onClick={evt => handlePlayerClick(evt, `${s.m}/${s.s}`, i)} >
              <Paper
                elevation={2}
                className={masterSlavePlayerWrapper(`${s.m}/${s.s}`)}
                ref={i === 0 ? setFirstSensorNode : null}  // measure the Paper itself (includes padding/border)
              >
                <Box height={sensorsHeights[`${s.m}/${s.s}`]} width={playerWidth} display="inline-block" >
                  <SensorPlayerToolbar
                    live={false}
                    missionIdOrName={s.m}
                    sensorIdOrName={s.s}
                    master={`${s.m}/${s.s}` === masterSensorName}
                  />
                  <SensorPlayer
                    ref={players[i]}
                    mode={'highQualityDvr'}
                    aspectRatioChangeBehaviour={'keepWidth'}
                    missionIdOrName={s.m}
                    sensorIdOrName={s.s}
                    serverUrl={server.serverHost}
                    useReverseProxy={server.useReverseProxy}
                    token={server.token}
                    clientId={server.clientId}
                    videoDir={"videos"}
                    playbackDataDelay={server.playbackDataDelay}
                    username={server.username}
                    password={server.password}
                    browserInfo={server.browserInfo}
                    onDataReceive={data => handleKlvDataReceived(`${s.m}/${s.s}`, i, s.platformType, data)}
                    onResolutionDetect={(width, height) => handleResolutionDetection(`${s.m}/${s.s}`, width, height)}
                    onDimensionsChange={(width, height) => handleDimensionsChange(`${s.m}/${s.s}`, width, height)}
                  />
                </Box>
                {
                  videoQueryChart && videoQueryChart.id === `${s.m}/${s.s}` &&
                  <div className={classes.playerBottomControls} >
                    <Box className={classes.videoCoverageChart}>
                      <VideoCoverageChart data={videoQueryChart.data} onClick={pos => handleVideoCoverageChartClick(i, pos)} />
                    </Box>
                  </div>
                }
                {bookmarksChartOpen &&
                  <div className={classes.playerBottomControls} >
                    <StBookmarksTimeline
                      serverUrl={server.serverHost}
                      token={server.token}
                      missionIdOrName={s.m}
                      sensorIdOrName={s.s}
                      bookmarksUpdateTime={bookmarksUpdateTime}
                      onBookmarkClick={bm => handleBookmarkChartClick(i, bm.position)}
                      onBookmarkAddClick={handleAddBookmark}
                      onBookmarkListClick={handleEditBookmarks}
                      theme={themeMode}
                    />
                  </div>
                }
                {exportVideotOpen &&
                  <div className={classes.playerBottomControls} >
                    <StCutControl serverUrl={server.serverHost} serverToken={server.token} missionIdOrName={s.m} sensorIdOrName={s.s} curPos={timelinePos} onExport={vid => handleExportVideo(s.m, s.s, vid)} theme={themeMode} />
                  </div>
                }
              </Paper>
            </Grid>
          ))}

          {props.mode === 'live' && props.sensors.filter(s => s.type === 'video').map((s, i) => (
            <Grid
              key={`${s.parentPlatformName}/${s.name}`}
              item
              onClick={evt => handlePlayerClick(evt, `${s.parentPlatformName}/${s.name}`, i)}
            >
              {/* The inner wrapper keeps measurement precise without affecting Grid spacing */}
              <div
                ref={i === 0 ? setFirstSensorNode : null}
                data-measure-inner={i === 0 ? '1' : undefined}
                style={{ display: 'block', margin: 0, padding: 0 }}
              >
                <LivePlayer
                  sensor={s}
                  index={i}
                  playerRef={players[i]}
                  playerWidth={playerWidth}
                  wrapperClass={masterSlavePlayerWrapper(`${s.parentPlatformName}/${s.name}`)}
                  masterSensorName={masterSensorName}
                  liveMode={liveSensorsMode[`${s.parentPlatformName}/${s.name}`] || null}
                  classes={classes}
                  videoQueryChart={videoQueryChart}
                  /* callbacks */
                  onPlayerClick={handlePlayerClick}
                  onLiveControlModeChange={handleLiveModeControlChange}
                  onLiveModeControlError={handleLiveModeControlError}
                  onKlvDataReceive={data =>
                    handleKlvDataReceived(
                      `${s.parentPlatformName}/${s.name}`,
                      i,
                      s.platformType,
                      data
                    )
                  }
                  onResolutionDetect={(w, h) =>
                    handleResolutionDetection(
                      `${s.parentPlatformName}/${s.name}`,
                      w,
                      h
                    )
                  }
                  onDimensionsChange={(w, h) =>
                    handleDimensionsChange(`${s.parentPlatformName}/${s.name}`, w, h)
                  }
                  onVideoCoverageChartClick={pos => handleVideoCoverageChartClick(i, pos)}
                />
              </div>
            </Grid>
          ))}


          {gridMapOpen &&
            <Grid key={`gridMap`} item >
              <Paper elevation={2} >
                <UavMap
                  width={playerWidth} height={mapHeight}
                  ref={m => gridMapControl.current = m}
                  platforms={mapPlatforms} symbols={[]} milSymbols={[]}
                  aisTargets={aisData || []}
                  annotations={annotationData}
                  selectedChannel={0}
                  showVmti={false} showTrail={true} trailDuration={20}
                  mapCenterMode={mapProps.mapCenterMode}
                  mapControlsMode={currentMapControlsMode}
                  aoiControlsMode={mapProps.aoiControlsMode}
                  customMap={null}
                  lastMapCenter={mapProps.center}
                  lastMapZoom={mapProps.zoom}
                  dragging={true}
                  selectedAis={props.selectedAis}
                  selectedAisTrail={selectedAisTrail}
                  onMapControlsModeChange={handleMapControlsModeChange}
                  onAoiControlsModeChange={handleAoiControlsModeChange}
                  onShowHeatmapClick={handleShowHeatmapClick}
                  onShowAreaClick={handleShowAreaClick}
                  onShowPathClick={handleShowPathClick}
                  onDrawAction={handleDrawAction}
                  onMouseOver={handleMapMouseOver}
                  onMouseLeave={handleMapMouseLeave}
                  onClose={handleMapClose}
                  onAisSelected={handleAisSelected}
                  onAnnotationSelected={handleAnnotationSelected}
                  onMapBoundingBoxChange={handleMapBoundingBoxChange}
                />
              </Paper>
            </Grid>
          }
          {bookmarksDlgOpen &&
            <BookmarksDlg currentSensor={masterSensor.current} bookmarksUpdateTime={bookmarksUpdateTime} onDeleteBookmarks={handleDeleteBookmarks} onClose={handleCloseEditBookmarks} />
          }
        </Grid>
        <Grid item xs={12} md={12} lg={12} className={classes.timeline} >
          {props.mode !== 'live' && props.timelineOpen &&
            <CurrentMissionTimeline missionName={props.mission} curPos={timelinePos} missionUpdateTime={props.missionUpdateTime} bookmarksUpdateTime={bookmarksUpdateTime} onTimelineCrosshairClick={handleTimelineCrosshairClick} onBookmarkClick={handleBookmarkTimelineClick} />
          }
        </Grid>
      </ResizeAwareGrid>

      {klvViewerOpen && (
        <FloatingWnd location={klvWndLocation} draggable={true} onLocationChange={location => handleLocationChange('klv', location)}>
            <div className={classes.telemetryContainer} style={{ width: '100%', height: '100%' }}>
              <KlvView data={klvData} title={masterSensor.current} theme={themeMode} />
            </div>
          </FloatingWnd>
      )}

      {mapViewerOpen && (
        <FloatingWnd location={mapWndLocation} onLocationChange={location => handleLocationChange('floatingMap', location)} draggable={mapDraggable}  >
          <div id="container" >
            <UavMap
              width={mapWndLocation.width} height={mapWndLocation.height}
              ref={m => floatingMapControl.current = m}
              platforms={mapPlatforms} symbols={[]} milSymbols={[]}
              aisTargets={aisData || []}
              annotations={annotationData}
              selectedChannel={0}
              showVmti={false} showTrail={false} trailDuration={20}
              mapCenterMode={mapProps.mapCenterMode}
              mapControlsMode={currentMapControlsMode}
              aoiControlsMode={mapProps.aoiControlsMode}
              customMap={null}
              lastMapCenter={mapProps.center}
              lastMapZoom={mapProps.zoom}
              dragging={true}
              selectedAis={props.selectedAis}
              selectedAisTrail={selectedAisTrail}
              onMapControlsModeChange={handleMapControlsModeChange}
              onAoiControlsModeChange={handleAoiControlsModeChange}
              onShowHeatmapClick={handleShowHeatmapClick}
              onShowAreaClick={handleShowAreaClick}
              onShowPathClick={handleShowPathClick}
              onDrawAction={handleDrawAction}
              onMouseOver={handleMapMouseOver}
              onMouseLeave={handleMapMouseLeave}
              onClose={handleMapClose}
              onAisSelected={handleAisSelected}
              onMapBoundingBoxChange={handleMapBoundingBoxChange}
            />
          </div>
        </FloatingWnd>
      )}
      {createBookmarkOpen &&
        <CreateBookmarkDlg player={players[masterSensorIndex.current].current} timestamp={klvData ? moment.unix(klvData['2'] / 1000 / 1000).format('DD MMM YYYY, HH:mm:ss.SSSZZ') : undefined} onCreateBookmark={handleCreateBookmark} onClose={handleCloseCreateBookmark} />
      }
      {createSnapshotOpen &&
        <CreateSnapshotDlg player={players[masterSensorIndex.current].current} timestamp={klvData ? moment.unix(klvData['2'] / 1000 / 1000).format('DD MMM YYYY, HH:mm:ss.SSSZZ') : undefined} onCreateSnapshot={handleSnapshotCreate} onClose={handleSnapshotClose} />
      }
    </ReactResizeDetector>
  );
}

MissionPlayerGrid.propTypes = {
  mode: PropTypes.oneOf(['vod', 'live']).isRequired,
  sensors: PropTypes.arrayOf(
    PropTypes.shape({
      m: PropTypes.string,
      s: PropTypes.string,
      parentPlatformName: PropTypes.string,
      name: PropTypes.string,
      videoInfo: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
      streamInfo: PropTypes.shape({
        streams: PropTypes.arrayOf(
          PropTypes.shape({ codec_type: PropTypes.string, width: PropTypes.number, height: PropTypes.number })
        )
      }),
      platformType: PropTypes.string,
      config: PropTypes.shape({ wsVideoStreamPort: PropTypes.number })
    })
  ).isRequired,
  mission: PropTypes.string,
  missionUpdateTime: PropTypes.number,
  timelineOpen: PropTypes.bool,
  controls: PropTypes.arrayOf(PropTypes.string),
  takeSnapshot: PropTypes.bool,
  selectedAis: PropTypes.number,
  selectedAnnotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  annotationUpdate: PropTypes.number,
  mapCenter: PropTypes.shape({
    center: PropTypes.arrayOf(PropTypes.number).isRequired,
    zoom: PropTypes.number,
  }),
  selectMapControl: PropTypes.string,
  onSnapshot: PropTypes.func,
  onSnapshotClose: PropTypes.func,
  onApplyQueryFilter: PropTypes.func,
  onAisSelected: PropTypes.func,
  onMapBoundingBoxChange: PropTypes.func,
  onAnnotationSelected: PropTypes.func,
};

export default MissionPlayerGrid;
